import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { buyersAPI } from '../../services/api';
import {
  Building2,
  MapPin,
  Globe,
  Mail,
  DollarSign,
  FlaskConical,
  Briefcase,
  Target,
  Loader2,
  Save,
  CheckCircle,
  AlertCircle,
  X,
  Pencil,
  ExternalLink,
} from 'lucide-react';

// Common therapeutic areas for suggestions
const THERAPEUTIC_AREAS = [
  'Oncology',
  'Immunology',
  'Neurology',
  'Cardiovascular',
  'Metabolic',
  'Rare Disease',
  'Infectious Disease',
  'Ophthalmology',
  'Dermatology',
  'Respiratory',
  'Gastroenterology',
  'Hematology',
];

// Common tech platforms for suggestions
const TECH_PLATFORMS = [
  'ADC',
  'Small Molecule',
  'Monoclonal Antibody',
  'Bispecific Antibody',
  'CAR-T',
  'Cell Therapy',
  'Gene Therapy',
  'mRNA',
  'siRNA',
  'PROTAC',
  'Peptide',
  'Radiopharmaceutical',
];

// BD Focus areas
const BD_FOCUS_OPTIONS = [
  'Licensing-In',
  'Licensing-Out',
  'Co-Development',
  'Acquisition',
  'Strategic Partnership',
  'Research Collaboration',
];

export default function BuyerProfile() {
  const navigate = useNavigate();
  const { userProfile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Profile data (for view mode)
  const [profileData, setProfileData] = useState(null);

  // Form state (for edit mode)
  const [formData, setFormData] = useState({
    company_name: '',
    headquarters: '',
    bd_contact_email: '',
    bd_page_url: '',
    revenue: '',
    therapeutic_areas: [],
    tech_platforms: [],
    bd_focus: [],
  });

  // Tag input states
  const [therapeuticInput, setTherapeuticInput] = useState('');
  const [platformInput, setPlatformInput] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await buyersAPI.getMyProfile();
      const data = response.data?.data;

      if (data?.hasProfile && data?.profile) {
        setHasProfile(true);
        setProfileData(data.profile);
        setFormData({
          company_name: data.profile.company_name || '',
          headquarters: data.profile.headquarters || '',
          bd_contact_email: data.profile.bd_contact_email || '',
          bd_page_url: data.profile.bd_page_url || '',
          revenue: data.profile.revenue || '',
          therapeutic_areas: data.profile.therapeutic_areas || [],
          tech_platforms: data.profile.tech_platforms || [],
          bd_focus: data.profile.bd_focus || [],
        });
        setIsEditing(false); // View mode by default when profile exists
      } else {
        setHasProfile(false);
        setIsEditing(true); // Edit mode when no profile
        // Pre-fill email from user profile if available
        if (userProfile?.email) {
          setFormData(prev => ({
            ...prev,
            bd_contact_email: userProfile.email,
          }));
        }
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTherapeuticArea = (area) => {
    if (area && !formData.therapeutic_areas.includes(area)) {
      setFormData(prev => ({
        ...prev,
        therapeutic_areas: [...prev.therapeutic_areas, area],
      }));
    }
    setTherapeuticInput('');
  };

  const handleRemoveTherapeuticArea = (area) => {
    setFormData(prev => ({
      ...prev,
      therapeutic_areas: prev.therapeutic_areas.filter(a => a !== area),
    }));
  };

  const handleAddPlatform = (platform) => {
    if (platform && !formData.tech_platforms.includes(platform)) {
      setFormData(prev => ({
        ...prev,
        tech_platforms: [...prev.tech_platforms, platform],
      }));
    }
    setPlatformInput('');
  };

  const handleRemovePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      tech_platforms: prev.tech_platforms.filter(p => p !== platform),
    }));
  };

  const handleToggleBdFocus = (focus) => {
    setFormData(prev => ({
      ...prev,
      bd_focus: prev.bd_focus.includes(focus)
        ? prev.bd_focus.filter(f => f !== focus)
        : [...prev.bd_focus, focus],
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    // Reset form data to saved profile data
    if (profileData) {
      setFormData({
        company_name: profileData.company_name || '',
        headquarters: profileData.headquarters || '',
        bd_contact_email: profileData.bd_contact_email || '',
        bd_page_url: profileData.bd_page_url || '',
        revenue: profileData.revenue || '',
        therapeutic_areas: profileData.therapeutic_areas || [],
        tech_platforms: profileData.tech_platforms || [],
        bd_focus: profileData.bd_focus || [],
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaveSuccess(false);

    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await buyersAPI.updateMyProfile(formData);
      const savedProfile = response.data?.data?.profile;

      setSaveSuccess(true);
      setHasProfile(true);
      setProfileData(savedProfile || formData);
      setIsEditing(false); // Switch to view mode after save

      // Refresh the auth profile to get the new buyer_id
      if (refreshProfile) {
        await refreshProfile();
      }

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // VIEW MODE
  // ============================================================================
  if (!isEditing && hasProfile && profileData) {
    return (
      <div className="max-w-3xl mx-auto">
        {/* Header with Edit Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
            <p className="text-gray-500 mt-1">Your BD profile visible to potential partners</p>
          </div>
          <button
            onClick={handleEdit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm font-medium text-green-800">
              Profile saved successfully!
            </p>
          </div>
        )}

        {/* Company Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          {/* Company Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{profileData.company_name}</h2>
                {profileData.headquarters && (
                  <p className="text-blue-100 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {profileData.headquarters}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div className="p-6 space-y-6">
            {/* Revenue */}
            {profileData.revenue && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Revenue Range</p>
                  <p className="font-medium text-gray-900">{profileData.revenue}</p>
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="grid md:grid-cols-2 gap-4">
              {profileData.bd_contact_email && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">BD Contact Email</p>
                    <a href={`mailto:${profileData.bd_contact_email}`} className="font-medium text-blue-600 hover:underline">
                      {profileData.bd_contact_email}
                    </a>
                  </div>
                </div>
              )}

              {profileData.bd_page_url && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">BD Webpage</p>
                    <a
                      href={profileData.bd_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      Visit Page <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Therapeutic Areas */}
        {profileData.therapeutic_areas?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-600" />
              Therapeutic Areas of Interest
            </h3>
            <div className="flex flex-wrap gap-2">
              {profileData.therapeutic_areas.map(area => (
                <span
                  key={area}
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Technology Platforms */}
        {profileData.tech_platforms?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Technology Platforms of Interest
            </h3>
            <div className="flex flex-wrap gap-2">
              {profileData.tech_platforms.map(platform => (
                <span
                  key={platform}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                >
                  {platform}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* BD Focus Areas */}
        {profileData.bd_focus?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              BD Focus Areas
            </h3>
            <div className="flex flex-wrap gap-2">
              {profileData.bd_focus.map(focus => (
                <span
                  key={focus}
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium"
                >
                  {focus}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================================================
  // EDIT MODE
  // ============================================================================
  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {hasProfile ? 'Edit Company Profile' : 'Complete Your Profile'}
          </h1>
          <p className="text-gray-500 mt-1">
            {hasProfile
              ? 'Update your company information'
              : 'Set up your company profile to start messaging sellers and receiving exclusive updates'}
          </p>
        </div>
        {hasProfile && (
          <button
            onClick={handleCancel}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Setup Required Banner */}
      {!hasProfile && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Profile Setup Required</p>
            <p className="text-sm text-amber-700 mt-1">
              Complete your company profile to unlock messaging, following companies, and receiving exclusive BD updates.
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Company Information
          </h2>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Pfizer, Roche, Novartis"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Headquarters
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  name="headquarters"
                  value={formData.headquarters}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., New York, USA"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Revenue Range
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  name="revenue"
                  value={formData.revenue}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select range</option>
                  <option value="$1B-$10B">$1B - $10B</option>
                  <option value="$10B-$50B">$10B - $50B</option>
                  <option value="$50B+">$50B+</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            BD Contact Information
          </h2>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BD Contact Email
              </label>
              <input
                type="email"
                name="bd_contact_email"
                value={formData.bd_contact_email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="bd@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BD Webpage URL
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  name="bd_page_url"
                  value={formData.bd_page_url}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://company.com/bd"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Therapeutic Areas */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-600" />
            Therapeutic Areas of Interest
          </h2>

          {/* Selected Tags */}
          {formData.therapeutic_areas.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.therapeutic_areas.map(area => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                >
                  {area}
                  <button
                    type="button"
                    onClick={() => handleRemoveTherapeuticArea(area)}
                    className="hover:text-purple-900"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input with suggestions */}
          <div className="relative">
            <input
              type="text"
              value={therapeuticInput}
              onChange={(e) => setTherapeuticInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTherapeuticArea(therapeuticInput.trim());
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type or select therapeutic areas..."
            />
          </div>

          {/* Quick Add Buttons */}
          <div className="mt-3 flex flex-wrap gap-2">
            {THERAPEUTIC_AREAS.filter(a => !formData.therapeutic_areas.includes(a)).map(area => (
              <button
                key={area}
                type="button"
                onClick={() => handleAddTherapeuticArea(area)}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors"
              >
                + {area}
              </button>
            ))}
          </div>
        </div>

        {/* Technology Platforms */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Technology Platforms of Interest
          </h2>

          {/* Selected Tags */}
          {formData.tech_platforms.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.tech_platforms.map(platform => (
                <span
                  key={platform}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {platform}
                  <button
                    type="button"
                    onClick={() => handleRemovePlatform(platform)}
                    className="hover:text-blue-900"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input with suggestions */}
          <div className="relative">
            <input
              type="text"
              value={platformInput}
              onChange={(e) => setPlatformInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddPlatform(platformInput.trim());
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type or select technology platforms..."
            />
          </div>

          {/* Quick Add Buttons */}
          <div className="mt-3 flex flex-wrap gap-2">
            {TECH_PLATFORMS.filter(p => !formData.tech_platforms.includes(p)).map(platform => (
              <button
                key={platform}
                type="button"
                onClick={() => handleAddPlatform(platform)}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors"
              >
                + {platform}
              </button>
            ))}
          </div>
        </div>

        {/* BD Focus */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            BD Focus Areas
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {BD_FOCUS_OPTIONS.map(focus => (
              <button
                key={focus}
                type="button"
                onClick={() => handleToggleBdFocus(focus)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.bd_focus.includes(focus)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {focus}
              </button>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-4">
          {hasProfile && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {hasProfile ? 'Save Changes' : 'Create Profile'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
